import { AppTheme, AppThemeType } from 'common/constants';
import { ErrorType, FormItem, IValidation, UIChipColor } from 'common/interfaces';

import { UiTheme } from 'common/interfaces/itheme';
import { isObject, toString } from 'lodash';
import moment from 'moment';
import { isValidNumber } from './string-utils';

export const isValidMobile = (mobileNo: string): boolean => RegExp(/^[0-9]{10}$/).test(mobileNo);

/**
 * createThread executes @threadCallback after @threadDelay
 * to allow the function to execute outside the normal flow
 * @param threadCallback
 * @param threadDelay
 */
export const createThread = (threadCallback: () => void, threadDelay: number = 0) => {
  const intervalId = setTimeout(threadCallback, threadDelay);
  return intervalId;
};

/** Converts an amount to INR format
 * Eg: 1234 -> 1,234.00, 1234567.8 -> 12,34,567.80
 */
export const roundFee = (amount?: number | string, removePaisaIfZero: boolean = true) => {
  if (isNaN(Number(amount?.toString())) || (!amount && amount !== 0)) {
    return '';
  }

  let amountString = amount.toString();
  const indexOfDecimal = amountString.indexOf('.');

  if (indexOfDecimal === -1) {
    amountString += '.00';
  } else {
    amountString += '00';
  }

  const amountSplit = amountString.split('.');
  if (amountSplit.length > 1) {
    amountSplit[1] = amountSplit[1].slice(0, 2);
  }

  const truncatedAmount = amountSplit.join('.');
  let formattedAmount = truncatedAmount.replace(/(\d)(?=(\d{2})+\d\.)/g, '$1,');

  if (removePaisaIfZero) {
    if (!(Math.abs(parseFloat(truncatedAmount)) % 1 > 0)) {
      formattedAmount = formattedAmount.slice(0, -3);
    }
  }

  return formattedAmount;
};

export const modifyToFormat = (date: string | number, format: string = 'hh:mm a, DD MMM YYYY') => {
  const momentDate = moment(date);
  return momentDate.format(format);
};

//converts yyyy-mm-dd to dd/mm/yyyy
export function reformatDate(dateStr: string) {
  const dArr = dateStr.split('-');
  return dArr[2] + '/' + dArr[1] + '/' + dArr[0];
}

export const getCurrentTimeStamp = () => new Date().getTime();

export const getCurrentTimeStampUTC = () => {
  const d = new Date();
  const diff = d.getTimezoneOffset();
  return d.getTime() + diff * 60 * 1000;
};
export const getCurrentTimeYear = () => new Date().getFullYear();

/**
 * return theme name from the colorScheme
 * @param colorScheme
 * @param defaultValue
 * @returns theme name
 */
export const getAppThemeFromColorScheme = (colorScheme: string, defaultValue: AppTheme) => {
  //TODO returning inverted theme as of now. as we dont have good light theme and for dev we have light device theme as default.
  //once we create both dark and light theme assets, revert the logic
  if (colorScheme === 'dark') {
    return AppTheme.LIGHT;
  } else if (colorScheme === 'light') {
    return AppTheme.LIGHT;
  }

  return defaultValue;
};

export const getSystemTheme = (colorScheme: string | undefined | null): UiTheme => {
  let themeToSet = null;
  const themeName = colorScheme ? getAppThemeFromColorScheme(colorScheme, AppTheme.DARK) : AppTheme.DARK;
  themeToSet = { name: themeName, type: AppThemeType.SYSTEM };
  return themeToSet;
};

export const numberToNthDisplay = function (num: number | undefined) {
  if (!num) {
    return '';
  }
  if (num > 3 && num < 21) return num + 'th';
  switch (num % 10) {
    case 1:
      return num + 'st';
    case 2:
      return num + 'nd';
    case 3:
      return num + 'rd';
    default:
      return num + 'th';
  }
};

export const numberToNthDisplaySuffix = function (num: number | undefined) {
  if (!num) {
    return '';
  }
  if (num > 3 && num < 21) return 'th';
  switch (num % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export function isNumeric(value: string) {
  return /^\d+$/.test(value);
}

export const getTruncatedString = (text: string, limit: number, ellipses: boolean) => {
  if (text && text.length > limit) {
    return text.substring(0, limit) + (ellipses ? '...' : '');
  }
  return text;
};

export const addDaysToDate = (date: Date, days: number) => {
  const nextDate = new Date(date);
  date.setDate(nextDate.getDate() + 2);
  return date.getTime();
};

export const shallowCopy = <T>(obj: T, toCopy: T): T => {
  if (!isObject(toCopy) || !isObject(obj)) return obj;
  for (let key in toCopy) {
    obj[key] = toCopy[key];
  }
  return obj;
};

export const noOp = () => {};

export const sleep = (ms: number = 5000, outcome: 'resolve' | 'reject' = 'resolve') => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (outcome === 'resolve') return resolve(true);
      else return reject(false);
    }, ms);
  });
};

export const formatMobileNumber = (mobileNumber: string) => {
  var cleaned = ('' + mobileNumber).replace(/\D/g, '');
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return null;
};

export const formatPhoneNumberInputText = (text: string, previousText: string) => {
  if (!text) return text;

  const deleting = previousText && previousText.length > text.length;
  if (deleting) {
    return text;
  }

  let cleaned = text.replace(/\D/g, ''); // remove non-digit characters
  let match = null;

  if (cleaned.length > 0 && cleaned.length < 2) {
    return `(${cleaned}`;
  } else if (cleaned.length == 3) {
    return `(${cleaned}) `;
  } else if (cleaned.length > 3 && cleaned.length < 5) {
    match = cleaned.match(/(\d{3})(\d{1,3})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}`;
    }
  } else if (cleaned.length == 6) {
    match = cleaned.match(/(\d{3})(\d{3})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-`;
    }
  } else if (cleaned.length > 6) {
    match = cleaned.match(/(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  }

  return cleaned;
};

export const formatDecimal = (text: string, decimalPoints: number = 2) => {
  let decimalRegEx = new RegExp(/^\d*\.?\d*$/);
  if (
    text.length === 0 ||
    text === '.' ||
    (text[text.length - 1] === '.' && decimalRegEx.test(text)) ||
    (text[text.length - decimalPoints] === '.' &&
      text[text.length - (decimalPoints - 1)] === '0' &&
      decimalRegEx.test(text))
  ) {
    return text.length == 1 ? '0' + text : text;
  } else {
    const distanceRegEx = new RegExp(/^-?[0-9]\d*(\.\d+)?$/);
    if (distanceRegEx.test(text)) {
      // if (text.indexOf('.') >= 0) {
      //   return Number(text).toFixed(2).toString();
      // }
      return Number(Number(text).toFixed(decimalPoints)).toString();
    }
  }
};

export const formatDecimalNumber = (num: number, decimalPoints: number = 2) => {
  return Number((num || 0).toFixed(2));
};

export const validateFormData = (items: FormItem[], data: any, itemKey?: string, value?: any, shortMsg?: boolean) => {
  let isValid = true;
  const temp = {} as ErrorType;
  if (data) {
    items
      .filter((item) => !itemKey || item.itemKey === itemKey)
      .forEach((item, index) => {
        const val = itemKey ? value : (data as any)[item.itemKey];
        if (item.required && !isValidNumber(val)) {
          isValid = false;
          temp[item.itemKey] = shortMsg ? 'Required' : item.label + ' is required';
        } else if (item.minLength && toString(val).length < item.minLength) {
          isValid = false;
          temp[item.itemKey] = 'Min. length of ' + item.minLength + ' is required.';
        } else if (item.maxLength && toString(val).length > item.maxLength) {
          isValid = false;
          temp[item.itemKey] = 'Should be less than or equal ' + item.maxLength + ' characters.';
        } else if ((item.validationType === 'numeric' || item.validationType === 'decimal') && isNaN(val)) {
          isValid = false;
          temp[item.itemKey] = 'Please enter valid numeric value.';
        } else if (item.validationType === 'time' && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(val)) {
          isValid = false;
          temp[item.itemKey] = 'Time should be in hh:mm format:' + val;
        } else {
          if (temp[item.itemKey]) {
            delete temp[item.itemKey];
          }
        }
      });
  }
  return { valid: isValid, error: temp } as IValidation;
};

export const getStatusChipBgColor = (status: string) => {
  switch (status) {
    case 'SUBMITTED':
      return 'orange';
    case 'UNDER_REVIEW':
      return '#BA1D22';
    case 'APPROVED':
      return '#589C4D';
    case 'DRAFT':
      return 'blue';
    default:
      return '#FF0002';
  }
};

export const getStatusChipTextColor = (status: string) => {
  switch (status) {
    case 'SUBMITTED':
      return 'black';
    default:
      return 'white';
  }
};
